import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = ["Idea", "Pre-Seed", "Seed", "Series A", "Series B+"];

const initialForm = {
  name: "",
  company_name: "",
  website: "",
  problem: "",
  revenue: "",
  stage: "Idea",
  deck_url: "",
  email: "",
  contact: "",
};

export default function ApplyForm() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!form.name || !form.company_name || !form.email || !form.problem || form.problem.length < 10) {
      toast.error("Please complete the required fields.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/applications`, form);
      setSubmitted(true);
      toast.success("Application received. We'll be in touch.");
      setForm(initialForm);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="apply"
      data-testid="apply-section"
      className="relative bg-black text-white border-t border-white/5"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-28 md:py-40">
        <div className="grid grid-cols-12 gap-y-12 md:gap-x-16">
          {/* Left intro */}
          <div className="col-span-12 md:col-span-4">
            <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
              §05 — Apply
            </p>
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            >
              Submit your <span className="italic font-light">company</span>.
            </motion.h2>
            <p className="mt-6 text-white/60 text-base font-light leading-relaxed max-w-sm">
              We review every submission personally. Founders shortlisted will
              hear from us within two weeks.
            </p>

            <div className="mt-10 border-t border-white/10 pt-8 text-xs uppercase tracking-[0.25em] text-white/40 space-y-2">
              <div>Confidential · Reviewed by partners</div>
              <div>Selective intake</div>
            </div>
          </div>

          {/* Form */}
          <div className="col-span-12 md:col-span-8">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                data-testid="apply-success"
                className="border border-white/20 p-10 md:p-16"
              >
                <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
                  Received
                </p>
                <h3 className="font-serif text-3xl md:text-5xl leading-tight">
                  Thank you. Your application is in <span className="italic font-light">review</span>.
                </h3>
                <p className="mt-6 text-white/60 max-w-md font-light">
                  We'll respond directly to {`${form.email || "your email"}`} if your company progresses to the next round.
                </p>
                <button
                  data-testid="apply-reset-button"
                  onClick={() => setSubmitted(false)}
                  className="mt-10 text-xs uppercase tracking-[0.25em] border-b border-white pb-1 hover:opacity-70"
                >
                  Submit another company
                </button>
              </motion.div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-10" data-testid="apply-form">
                <Row>
                  <Field
                    label="Founder Name"
                    required
                    testid="field-name"
                    value={form.name}
                    onChange={(v) => setField("name", v)}
                    placeholder="Your full name"
                  />
                  <Field
                    label="Email"
                    required
                    type="email"
                    testid="field-email"
                    value={form.email}
                    onChange={(v) => setField("email", v)}
                    placeholder="you@company.com"
                  />
                </Row>

                <Row>
                  <Field
                    label="Company Name"
                    required
                    testid="field-company"
                    value={form.company_name}
                    onChange={(v) => setField("company_name", v)}
                    placeholder="Company"
                  />
                  <Field
                    label="Website"
                    testid="field-website"
                    value={form.website}
                    onChange={(v) => setField("website", v)}
                    placeholder="https://"
                  />
                </Row>

                <Field
                  label="What problem are you solving?"
                  required
                  multiline
                  testid="field-problem"
                  value={form.problem}
                  onChange={(v) => setField("problem", v)}
                  placeholder="In 2–4 sentences, describe the structural problem you're solving and why now."
                />

                <Row>
                  <Field
                    label="Current Revenue"
                    testid="field-revenue"
                    value={form.revenue}
                    onChange={(v) => setField("revenue", v)}
                    placeholder="e.g. $0 / $50k MRR / $1.2M ARR"
                  />
                  <SelectField
                    label="Stage"
                    required
                    testid="field-stage"
                    options={STAGES}
                    value={form.stage}
                    onChange={(v) => setField("stage", v)}
                  />
                </Row>

                <Row>
                  <Field
                    label="Deck URL"
                    testid="field-deck"
                    value={form.deck_url}
                    onChange={(v) => setField("deck_url", v)}
                    placeholder="Google Drive / Notion / Dropbox link"
                  />
                  <Field
                    label="Phone or Additional Contact"
                    testid="field-contact"
                    value={form.contact}
                    onChange={(v) => setField("contact", v)}
                    placeholder="Optional"
                  />
                </Row>

                <div className="pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                  <p className="text-xs uppercase tracking-[0.25em] text-white/40 max-w-sm">
                    By submitting you consent to Runway Foundry storing your application securely.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="apply-submit-button"
                    className="group inline-flex items-center justify-center gap-3 bg-white text-black px-10 py-4 text-xs uppercase tracking-[0.25em] font-medium hover:bg-white/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Submitting…" : "Submit Application"}
                    <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-10">{children}</div>;
}

function Field({ label, value, onChange, placeholder, type = "text", required, multiline, testid }) {
  const sharedClasses =
    "w-full bg-transparent border-b border-white/15 py-3 text-white placeholder:text-white/25 focus:border-white focus:outline-none transition-colors text-base font-light";
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">
        {label}
        {required && <span className="text-white/70"> *</span>}
      </span>
      {multiline ? (
        <textarea
          data-testid={testid}
          required={required}
          rows={4}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`${sharedClasses} resize-none mt-3`}
        />
      ) : (
        <input
          data-testid={testid}
          required={required}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`${sharedClasses} mt-3`}
        />
      )}
    </label>
  );
}

function SelectField({ label, value, onChange, options, required, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">
        {label}
        {required && <span className="text-white/70"> *</span>}
      </span>
      <select
        data-testid={testid}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-3 w-full bg-transparent border-b border-white/15 py-3 text-white focus:border-white focus:outline-none transition-colors text-base font-light appearance-none cursor-pointer"
        style={{ backgroundImage: "none" }}
      >
        {options.map((o) => (
          <option key={o} value={o} className="bg-black text-white">
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
